/** @jsx jsx */
import { jsx } from '@front10/base-elements';
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import { Context } from '@front10/helpers/dist/Context';
import { useEffect, useContext } from 'react';
import DatePicker from '../DatePicker';
import Box from '../../elements/Box';
import {
  isPastDate,
  isCurrentDay,
  isSameDate,
  isOutsideDay
} from '../../../helpers/dayCard/dayCard';
import ErrorBoundary from '../../utils/ErrorBoundary';
import Row from '../../elements/Row';
import Col from '../../elements/Col';
import LegendCalendar from '../../controls/TripFareCalendar/atoms/Legend';
import FareItemMediumSize from '../../../components/items/FareItemMediumSize';
import { useFareItemProps } from './../../../../src/components/containers/_commons/tracking';
import OverlayLayer from './atoms/OverlayLayer/OverlayLayer';
import useA11y from './hooks/a11y';
import isBetween from 'dayjs/plugin/isBetween';
import { useFlightCalendar, ONE_WAY, getIsDisregarded } from './hooks/flightCalendar';
import { Tooltip } from 'react-tooltip';

function FareCalendar(props) {
  const {
    initialView = false,
    overlayLayerSettings = {},
    overlayLayerErrorSettings = {},
    filtersState,
    displayErrorView,
    setDisplayErrorView,
    errorStatus = false,
    showLegend = true,
    isMobile = false,
    fareItemAtomSettings,
    calendarContainerSettings = {},
    globalDispatch,
    loading,
    combinable,
    tenantCode,
    numberOfMonthsToDisplay = 1,
    callToActionTooltipSettings = {},
    sumarySectionSettings = {}
  } = props;
  const isOriginSelected = Boolean(props.filtersState.locations.origin.value);
  const isDestinationSelected = Boolean(props.filtersState.locations.destination.value);
  const { direction, translate } = useContext(Context);

  dayjs.extend(isBetween);

  const {
    state,
    dispatch,
    handlerDayClick,
    handlerMonthNavigation,
    handlerDataChange,
    getSettingsFareItemMediumSize,
    handlerDayHover,
    restState
  } = useFlightCalendar({ globalDispatch, combinable, tenantCode });
  const { handleKeyboardNavigation } = useA11y({ dispatch, translate });

  useEffect(() => {
    if (errorStatus) {
      if (filtersState.locations.origin?.value) {
        filtersState.locations.origin?.onClear();
      }
      if (filtersState.locations.destination?.value) {
        filtersState.locations.destination?.onClear();
      }
      setDisplayErrorView(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorStatus]);

  useEffect(() => {
    const originValue = props.filtersState.locations.origin.value;
    const destinationValue = props.filtersState.locations.destination.value;
    if (displayErrorView && originValue && destinationValue) {
      setDisplayErrorView(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filtersState.locations.origin.value, props.filtersState.locations.destination.value]);

  useEffect(() => {
    dispatch({
      type: 'SET_CHANGE_FLIGHT_TYPE_FILTER',
      payload: { filterName: 'flightType', value: props.filtersState.flightType.value }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filtersState.flightType.value]);

  useEffect(() => {
    dispatch({
      type: 'SET_CHANGE_TRAVEL_CLASS_FILTER',
      payload: { filterName: 'travelClass', value: props.filtersState.travelClass.value }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filtersState.travelClass.value]);

  useEffect(() => {
    dispatch({
      type: 'SET_CHANGE_REDEMPTION_UNIT_FILTER',
      payload: { filterName: 'redemptionUnit', value: props.filtersState.redemptionUnit.value }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filtersState.redemptionUnit.value]);

  useEffect(() => {
    dispatch({
      type: 'SET_CHANGE_NUMBER_OF_STOP_FILTER',
      payload: { filterName: 'flightStopCount', value: props.filtersState.flightStopCount.value }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filtersState.flightStopCount.value]);

  useEffect(() => {
    restState(currentMonth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.filtersState.locations.origin.value, props.filtersState.locations.destination.value]);

  useEffect(() => {
    const date = props.thresholdDayEnable
      ? dayjs().add(props.thresholdDay, 'day').startOf('month').toDate()
      : props.defaultMonth
      ? dayjs().add(props.defaultMonth, 'month').startOf('month').toDate()
      : dayjs().toDate();
    dispatch({ type: 'SET_FIRST_DAY', payload: date });
    dispatch({ type: 'SET_FOCUS_DATE', payload: date });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toMonth = dayjs()
    .add(props.numberOfMonths - 1, 'month')
    .toDate();

  const onSelectNavMonth = (item) => {
    const { onSelectMonth, initialMonth } = props;
    handlerMonthNavigation({
      item,
      onSelectMonth,
      initialMonth,
      flightType: filtersState.flightType.value,
      dateRange: filtersState.dateRange,
      isMobile
    });
  };

  React.useEffect(() => {
    const elements = document?.querySelectorAll('.DayPicker-Day');
    if (!isMobile) {
      elements.forEach((element) => {
        if (element !== null && element !== undefined) {
          element.removeAttribute('role');
          element.removeAttribute('aria-label');
          element.removeAttribute('aria-disabled');
          element.removeAttribute('aria-selected');
          element.removeAttribute('tabindex');
        }
      });
    }
    handlerDataChange(
      props.data,
      props.filtersState.flightType.value,
      isMobile,
      props.filtersState
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.data]);

  const renderDay = (day, isFirstDay, isLastDay, dayModifiers) => {
    const {
      dayCard,
      data,
      loading,
      DayComponentInstance,
      currentMonth,
      disregardedBehavior
    } = props;

    const getFareItem = (fare) => {
      if (state.showCalendarEmpty) {
        if (combinable) {
          if (dayjs(day).isSame(state.departureDate, 'day')) {
            return state.departureFare?.fareItem;
          } else if (dayjs(day).isSame(state.returnDate, 'day')) {
            return state.fare?.fareItem;
          } else {
            return fare;
          }
        } else {
          if (dayjs(day).isSame(state.returnDate, 'day')) {
            return state.fare?.fareItem;
          } else {
            return fare;
          }
        }
      }
      return fare;
    };
    const getFare = (fare) => {
      if (state.showCalendarEmpty) {
        if (combinable) {
          if (dayjs(day).isSame(state.departureDate, 'day')) {
            return state.departureFare;
          } else if (dayjs(day).isSame(state.returnDate, 'day')) {
            return state.fare;
          } else {
            return fare;
          }
        } else {
          if (dayjs(day).isSame(state.returnDate, 'day')) {
            return state.fare;
          } else {
            return fare;
          }
        }
      }
      return fare;
    };

    let fare = data.filter(
      (item) => dayjs(day).format('MM/DD/YYYY') === dayjs(item.day).format('MM/DD/YYYY')
    )[0];
    const isLowest = fare ? getFare(fare)?.isLowest : null;
    const isToday = isCurrentDay(day);
    let isOutside;
    if (isMobile) {
      isOutside = isOutsideDay(day, currentMonth);
    } else {
      isOutside = dayModifiers?.outside ?? false;
    }
    fare = fare ? fare.fareItem : null;
    const style = Object.assign({}, dayCard.style);
    const isOneWayTrip = filtersState.flightType.value === ONE_WAY;

    const isDisregarded = getIsDisregarded(
      day,
      isMobile
        ? currentMonth
        : !isOneWayTrip && !loading && state.confirmedChangeDepartureDate
        ? state.departureDate
        : currentMonth,
      state.departureFare,
      state.fare,
      flightTypeValue,
      isMobile
        ? state.returnDate !== undefined
        : state.returnDate && state.mobileReturnDateConfirmed?.status === 'CONFIRMED'
    );

    const isBlackoutDate = fare !== null ? fare.isBlackoutDay : false;
    const firstDayToRender = dayjs(state.firstDay);
    const endDayToRender = dayjs(state.firstDay).endOf('month');

    const isDateInRange = (day) => {
      if (!isOneWayTrip) {
        if (
          !loading &&
          state.departureDate &&
          state.returnDate &&
          state.mobileDepartureDateConfirmed?.status === 'CONFIRMED' &&
          state.mobileReturnDateConfirmed?.status === 'CONFIRMED'
        ) {
          return (
            dayjs(day).isBetween(dayjs(state.departureDate), dayjs(state.returnDate), '()') &&
            !isOutside &&
            !isPastDate(day)
          );
        }
        if (state.departureDate && !state.returnDate && state.lastHoveredDate) {
          return (
            dayjs(day).isAfter(dayjs(state.departureDate), 'day') &&
            dayjs(day).isSameOrBefore(dayjs(state.lastHoveredDate), 'day')
          );
        } else if (state.departureDate && state.returnDate) {
          return dayjs(day).isBetween(dayjs(state.departureDate), dayjs(state.returnDate), '()');
        }
      }
      return false;
    };

    const isSelectedDate = (day) => {
      if (isOneWayTrip) {
        if (state.departureDate) {
          return isSameDate(day, state.departureDate);
        }
      } else {
        if (!isMobile) {
          if (state.departureDate && !state.returnDate) {
            return isSameDate(day, state.departureDate);
          }
          if (state.departureDate && state.returnDate) {
            return isSameDate(day, state.departureDate) || isSameDate(day, state.returnDate);
          }
        }
      }
      return false;
    };

    const getShowDayEmpty = () => {
      if (state.showCalendarEmpty) {
        if (combinable) {
          if (
            dayjs(day).isSame(state.returnDate, 'day') ||
            dayjs(day).isSame(state.departureDate, 'day')
          ) {
            return false;
          } else {
            return true;
          }
        } else {
          if (
            dayjs(day).isSame(state.returnDate, 'day') ||
            (state.departureDate &&
              !state.returnDate &&
              dayjs(day).isSame(state.departureDate, 'day') &&
              loading)
          ) {
            return false;
          } else {
            return true;
          }
        }
      } else {
        return false;
      }
    };

    const dayRender = (
      <DayComponentInstance
        {...dayCard}
        day={day}
        showDayEmpty={getShowDayEmpty()}
        isOutside={isOutside}
        isLowest={isLowest}
        isToday={isToday}
        isDisregarded={isDisregarded}
        isFirstDay={isSameDate(day, currentMonth)}
        showAsEmptyDay={initialView}
        isOneWayTrip={isOneWayTrip}
        isSelectedStartDate={dayjs(day).isSame(state.departureDate, 'day')}
        dayBeforeDeparture={
          !isOneWayTrip && state.departureDate && dayjs(day).isBefore(state.departureDate, 'day')
        }
        showRoundTripPriceLabel={
          !combinable && !isOneWayTrip && state.returnDate
            ? isSameDate(day, state.returnDate)
            : false
        }
        showCombinableDepartureLabel={
          combinable && !isOneWayTrip && state.departureDate
            ? isSameDate(day, state.departureDate)
            : false
        }
        showCombinablereturnLabel={
          combinable && !isOneWayTrip && state.returnDate
            ? isSameDate(day, state.returnDate)
            : false
        }
        selectedStartDate={
          !isOneWayTrip && state.departureDate && isSameDate(day, state.departureDate)
        }
        highlightDeparture={
          !isOneWayTrip &&
          state.departureDate &&
          isSameDate(day, state.departureDate) &&
          state.lastHoveredDate &&
          !isSameDate(state.lastHoveredDate, state.departureDate)
        }
        islastHoveredDate={
          state.lastHoveredDate !== undefined && isSameDate(state.lastHoveredDate, day)
        }
        lastSelectedDate={state.lastSelectedDate}
        selectedEndDate={!isOneWayTrip && state.returnDate && isSameDate(day, state.returnDate)}
        isSelectedDepartureDate={!isOneWayTrip && state.departureDate}
        confirmedDate={
          state.departureDate &&
          state.returnDate &&
          state.mobileDepartureDateConfirmed?.status === 'CONFIRMED' &&
          state.mobileReturnDateConfirmed?.status === 'CONFIRMED' &&
          state.mobileDepartureDateConfirmed?.date &&
          state.mobileReturnDateConfirmed?.date &&
          (dayjs(day).isSame(state.mobileDepartureDateConfirmed?.date, 'day') ||
            dayjs(day).isSame(state.mobileReturnDateConfirmed?.date, 'day'))
        }
        departureFare={state.departureFare}
        sameDepartureAndReturnDate={
          state.departureDate &&
          state.returnDate &&
          dayjs(state.departureDate).isSame(state.returnDate, 'day')
        }
        combinable={combinable}
        returnFare={state.fare}
        selectedDay={isSelectedDate(day)}
        isBlackoutDay={isBlackoutDate}
        isInDateRange={isDateInRange(day)}
        isFirstDateFocusable={isSameDate(day, firstDayToRender)}
        data={getFareItem(fare)}
        loading={loading}
        firstDay={firstDayToRender}
        endDay={endDayToRender}
        style={style}
        direction={direction}
        disregardedBehavior={disregardedBehavior}
        accessibleByArrowKeys={isOriginDestinationFilled}
        focusDate={state.focusDate}
        avoidFocusDateAction={state.avoidFocusDateAction}
        handleKeyboardNavigation={(day, e) => {
          handleKeyboardNavigation(day, e, state.firstDay, state.focusDate, data);
        }}
        handlerDayHover={(day) => {
          if (!isOutside) {
            handlerDayHover(day);
          }
        }}
        handlerDayClick={(day, e) => {
          if (!isBlackoutDate && !isOutside && !isPastDate(day)) {
            if (fare !== null) {
              const newfare = props.data.filter(
                (item) => dayjs(day).format('MM/DD/YYYY') === dayjs(item.day).format('MM/DD/YYYY')
              )[0];
              handlerDayClick({
                date: day,
                fare: newfare,
                isOneWayTrip,
                event: e,
                dateRange: filtersState.dateRange,
                numberOfMonths: props.numberOfMonths,
                isMobile
              });
            } else {
              handlerDayClick({
                date: day,
                fare: {
                  day: day,
                  fareItem: {
                    dataType: 'EMPTY_FROM_API',
                    departureDate: dayjs(day).format('YYYY-MM-DD')
                  }
                },
                isOneWayTrip,
                event: e,
                dateRange: filtersState.dateRange,
                numberOfMonths: props.numberOfMonths,
                isMobile
              });
            }
          }
        }}
      />
    );

    return dayRender;
  };

  const {
    NavComponentInstance,
    numberOfMonths,
    navComponent,
    datePicker,
    usedErrorBoundary,
    initialMonth,
    currentMonth,
    dayCard
  } = props;

  const itemSettings = useFareItemProps(state.fare, globalDispatch, null);
  const isOriginDestinationFilled = isOriginSelected && isDestinationSelected;
  const travelClassValue =
    filtersState.travelClass?.value?.lenth !== 0
      ? filtersState.travelClass?.value[0]
      : filtersState.travelClass?.defaultValue;
  const flightTypeValue =
    filtersState.flightType?.value ?? filtersState.flightTypeValue?.defaultValue;
  const redemptionUnit =
    filtersState.redemptionUnit?.value ?? filtersState.redemptionUnit?.defaultValue;

  const navigation = NavComponentInstance && (
    <NavComponentInstance
      months={numberOfMonths}
      fromMonth={initialMonth}
      showAsSelect={true}
      disablePrevButton={flightTypeValue !== ONE_WAY}
      selectedDepartureDate={state.departureDate}
      lastSelectedReturnDate={state.lastSelectedDate === 'RETURN_DATE'}
      {...navComponent}
      onSelect={onSelectNavMonth}
      currentMonth={currentMonth}
      disableAction={loading}
      enableDropDown={isOriginDestinationFilled}
    />
  );

  const calendar = (
    <DatePicker
      {...datePicker}
      captionElement={() => null}
      canChangeMonth={false}
      month={currentMonth}
      fromMonth={initialMonth}
      toMonth={toMonth}
      renderDay={renderDay}
      numberOfMonths={numberOfMonthsToDisplay}
    />
  );

  const getFareItemMediumSizeProps = (settings) => {
    let newSettings = { ...settings };
    if (
      (state?.fare?.fareItem?.dataType === 'DATA_API' && !combinable) ||
      (state?.fare?.fareItem?.dataType === 'DATA_API' &&
        combinable &&
        filtersState.flightType.value === ONE_WAY)
    ) {
      newSettings = {
        ...newSettings,
        data: {
          ...newSettings.data,
          formattedTotalPrice: newSettings.data.formattedTotalPriceForMobile,
          shortenedFormattedTotalPrice: newSettings.data.shortenedFormattedTotalPriceForMobile
        }
      };
    }
    if (
      state?.fare?.fareItem?.dataType === 'DATA_API' &&
      state?.departureFare?.fareItem?.dataType === 'DATA_API' &&
      combinable &&
      filtersState.redemptionUnit.value === 'CASH' &&
      filtersState.flightType.value === 'ROUND_TRIP'
    ) {
      newSettings = {
        ...newSettings,
        data: {
          ...newSettings.data,
          formattedTotalPrice: `${
            newSettings.data.currencySymbol ? newSettings.data.currencySymbol : ''
          }${newSettings.data.formattedTotalPrice}`,
          shortenedFormattedTotalPrice: `${
            newSettings.data.currencySymbol ? newSettings.data.currencySymbol : ''
          }${newSettings.data.shortenedFormattedTotalPrice}`
        }
      };
    }
    return newSettings;
  };

  const legend = (
    <Row {...props.legendRowSettings}>
      <Col md="12" {...props.legendContentSettings}>
        <LegendCalendar
          {...{ ...props.legend, highlightLowestPrices: dayCard?.highlightLowestPrices }}
          isOneWayTrip={filtersState.flightType.value === ONE_WAY}
        />
      </Col>
    </Row>
  );

  const fareCalendarRender = (
    <Box width="100" className="fr-fare-calendar">
      <Box>
        {!isMobile && showLegend && <>{legend}</>}
        {navigation}
        <Box
          style={{ display: 'flex', flexDirection: 'column', position: 'relative' }}
          {...calendarContainerSettings}
        >
          {displayErrorView && <OverlayLayer {...overlayLayerErrorSettings} />}
          {initialView && !displayErrorView && <OverlayLayer {...overlayLayerSettings} />}
          {calendar}
        </Box>
      </Box>
      {isMobile && showLegend && <>{legend}</>}
      <Row>
        <Col sm="12" {...sumarySectionSettings}>
          {(flightTypeValue === 'ROUND_TRIP'
            ? state?.departureDate === undefined || state?.returnDate === undefined
            : state?.departureDate === undefined) && (
            <Tooltip {...callToActionTooltipSettings}>
              <Box>
                {flightTypeValue === 'ROUND_TRIP'
                  ? translate('cmp-call-to-action-tooltip-rt', 'Select a return date to book')
                  : translate('cmp-call-to-action-tooltip-ow', 'Select a date to book')}
              </Box>
            </Tooltip>
          )}
          {(!isMobile || (isMobile && isOriginDestinationFilled)) && (
            <FareItemMediumSize
              {...getFareItemMediumSizeProps({
                ...props.fareItemAtomSettings,
                ...itemSettings,
                ...getSettingsFareItemMediumSize({
                  fareItemAtomSettings,
                  isOriginDestinationFilled,
                  travelClassValue,
                  flightTypeValue,
                  redemptionUnit,
                  isMobile,
                  loading
                })
              })}
            />
          )}
        </Col>
      </Row>
    </Box>
  );

  return usedErrorBoundary ? (
    <ErrorBoundary>{fareCalendarRender}</ErrorBoundary>
  ) : (
    { fareCalendarRender }
  );
}

FareCalendar.propTypes = {
  /**
   * Component to show in navbar
   */
  NavComponentInstance: PropTypes.func.isRequired,
  /**
   * Component to show in cad
   */
  DayComponentInstance: PropTypes.func.isRequired,
  /**
   * Months to show
   */
  numberOfMonths: PropTypes.number,
  /**
   * NavComponentInstance styles props
   */
  navComponent: PropTypes.objectOf(PropTypes.any),
  /**
   * dayCard component props
   */
  renderNavInfo: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),

  dayCard: PropTypes.objectOf(PropTypes.any),
  /**
   * Array of fares to show in calendar
   */
  data: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)),
  /**
   * Fires when month selected is changed
   */
  onSelectMonth: PropTypes.func,
  /**
   * Define if conponent is loading
   */
  loading: PropTypes.bool,
  /**
   * DatePicker settings
   */
  datePicker: PropTypes.objectOf(PropTypes.any),
  /**
   * Define if is desktop
   */
  isMobile: PropTypes.bool,
  /**
   * Determine if the component is protected using ErrorBoundary.
   * By default all components protect themselves
   */
  usedErrorBoundary: PropTypes.bool,
  /**
   * Define the initial month of the calendar
   */
  initialMonth: PropTypes.instanceOf(Date),
  currentMonth: PropTypes.instanceOf(Date),
  disregardedBehavior: PropTypes.oneOf(['blank-date', 'past-date', 'glass-date'])
};
FareCalendar.defaultProps = {
  numberOfMonths: 3,
  navComponent: {},
  dayCard: {},
  data: [],
  onSelectMonth: () => {},
  renderNavInfo: null,
  loading: false,
  usedErrorBoundary: true, // By default all components protect themselves
  datePicker: {},
  isMobile: true,
  initialMonth: dayjs().toDate(),
  currentMonth: dayjs().toDate(),
  disregardedBehavior: 'past-date'
};

FareCalendar.contextType = Context;

FareCalendar.componentName = 'FareCalendar';

export default memo(
  FareCalendar,
  (prevProps, nextProps) =>
    prevProps.data === nextProps.data &&
    prevProps.loading === nextProps.loading &&
    prevProps.renderNavInfo === nextProps.renderNavInfo
);
